/**
 * Check if the new OTN implementation should be used
 * @returns {boolean} true if new implementation should be used, false for old implementation
 */
export const isNewOtnImplementation = () => {
  const envFlag = process.env.REACT_APP_OTN_SNMP_IMPLEMENTATION;
  return envFlag === 'true' || envFlag === '1';
};

/**
 * Check if alarm row uses the endpoint-based OTN live/history alarm shape
 * @param {Object} alarm
 * @returns {boolean}
 */
export const isOtnEndpointAlarm = (alarm) =>
  Boolean(alarm?.end_a_alarm_id || alarm?.end_b_alarm_id);

const getEndpointActiveTimestamps = (alarm) =>
  [
    alarm?.end_a_status === 'Red' ? alarm?.end_a_alarm_active_ts : null,
    alarm?.end_b_status === 'Red' ? alarm?.end_b_alarm_active_ts : null,
  ].filter(Boolean);

const getEndpointInactiveTimestamps = (alarm) =>
  [
    alarm?.end_a_status === 'Red'
      ? alarm?.end_a_alarm_inactive_ts || alarm?.end_a_alarm_active_ts
      : null,
    alarm?.end_b_status === 'Red'
      ? alarm?.end_b_alarm_inactive_ts || alarm?.end_b_alarm_active_ts
      : null,
  ].filter(Boolean);

/**
 * Get the appropriate timestamp field based on implementation
 * @param {Object} alarm - The alarm object
 * @returns {string} The timestamp to display
 */
export const getAlarmTimestamp = (alarm) => {
  const isNewImplementation = isNewOtnImplementation();
  const isOtnTab = sessionStorage.getItem('selectedTab') === 'otn';

  if (isOtnTab && isNewImplementation) {
    if (isOtnEndpointAlarm(alarm)) {
      const inactiveTimestamps = getEndpointInactiveTimestamps(alarm);
      if (inactiveTimestamps.length) {
        return inactiveTimestamps.sort((a, b) => new Date(b) - new Date(a))[0];
      }

      const activeTimestamps = getEndpointActiveTimestamps(alarm);
      if (!activeTimestamps.length) {
        return null;
      }

      return activeTimestamps.sort((a, b) => new Date(b) - new Date(a))[0];
    }

    return alarm?.alarm_active_ts;
  }

  return alarm?.last_occurred;
};

/**
 * Get the appropriate first occurred timestamp based on implementation
 * @param {Object} alarm - The alarm object
 * @returns {string} The first occurred timestamp
 */
export const getFirstOccurredTimestamp = (alarm) => {
  const isNewImplementation = isNewOtnImplementation();
  const isOtnTab = sessionStorage.getItem('selectedTab') === 'otn';

  if (isOtnTab && isNewImplementation) {
    if (isOtnEndpointAlarm(alarm)) {
      const activeTimestamps = getEndpointActiveTimestamps(alarm);
      if (!activeTimestamps.length) {
        return alarm?.end_a_alarm_active_ts || alarm?.end_b_alarm_active_ts || null;
      }

      return activeTimestamps.sort((a, b) => new Date(a) - new Date(b))[0];
    }

    return alarm?.alarm_active_ts;
  }

  return alarm?.first_occurred;
};

/**
 * Get the appropriate last occurred timestamp based on implementation
 * @param {Object} alarm - The alarm object
 * @returns {string} The last occurred timestamp
 */
export const getLastOccurredTimestamp = (alarm) => {
  const isNewImplementation = isNewOtnImplementation();
  const isOtnTab = sessionStorage.getItem('selectedTab') === 'otn';

  if (isOtnTab && isNewImplementation) {
    if (isOtnEndpointAlarm(alarm)) {
      const inactiveTimestamps = getEndpointInactiveTimestamps(alarm);
      if (!inactiveTimestamps.length) {
        return null;
      }

      return inactiveTimestamps.sort((a, b) => new Date(b) - new Date(a))[0];
    }

    return alarm?.alarm_inactive_ts;
  }

  return alarm?.last_occurred;
};

export const getOtnEndpointStatusStyle = (status) => ({
  background: status === 'Red' ? '#FFD5D7' : '#D4F5E4',
  color: status === 'Red' ? '#FF6066' : '#00B17B',
});
